import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import didUpdate from 'webapp/modifiers/did-update';
import FormInputDecimalComponent from 'webapp/components/form/input-decimal';

import type Block from 'webapp/models-custom/block';
import type BlockSetup from 'webapp/models-custom/block-setup';
import type DataSelect from 'webapp/models-custom/data-select';
import type VisualizationGauge from 'webapp/models-custom/visualization/gauge';
import type VisualizationProgress from 'webapp/models-custom/visualization/progress';
import type VisualizationRadialProgress from 'webapp/models-custom/visualization/radial-progress';

interface Args {
	blockM: Block;
	model: DataSelect;
}

export default class BlockSettingsMetricMaxValueComponent extends Component<Args> {
	@tracked updateInputDecimalValue = false;
	@tracked updatedValue: number | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.setUpdatedValue();
	}

	get block(): Block {
		return this.args.blockM;
	}

	get blockSetup(): BlockSetup {
		return this.block.blockSetup;
	}

	get visualization() {
		return this.blockSetup.visualization as
			VisualizationGauge | VisualizationProgress | VisualizationRadialProgress;
	}

	get currentValueString() {
		return this.updatedValue?.toString() || '0';
	}

	@action
	updateOnOutsideValueChange() {
		this.setUpdatedValue();

		this.updateInputDecimalValue = true;

		next(() => {
			this.updateInputDecimalValue = false;
		});
	}

	@action
	onValueChange(value: number | null) {
		this.updatedValue = value || 0;
	}

	@action
	commitValueChange() {
		const valueToSave = this.updatedValue;
		const dataSelect = this.args.model;

		// not saving invalid or 0 default
		dataSelect.startValue = valueToSave || null;
		void dataSelect.saveAndCatch();
	}

	private setUpdatedValue() {
		this.updatedValue = this.args.model.startValue || 0;
	}

	<template>
		<div class="row" {{didUpdate this.updateOnOutsideValueChange @model.startValue}}>
			<label class="right--half">Min value</label>
			<div class="right--half">
				<FormInputDecimalComponent
					@value={{this.currentValueString}}
					@onChange={{this.onValueChange}}
					@onFocusOut={{this.commitValueChange}}
					@forceUpdateFromValue={{this.updateInputDecimalValue}}
				/>
			</div>
		</div>
	</template>
}
